import { createSlice } from "@reduxjs/toolkit";
import type { PageSliceType } from "../../types/page/page.model";
import { axiosClient } from "../../configs/axiosClient";
import { API } from "../../configs/endpoints";
import { addAsyncCases, createApiThunk } from "../createApiThunk";
import type {
  CreatePageResponse,
  GetPageResponse,
  ListPageResponse,
  UpdatePageResponse,
} from "../../types/page/page.response";
import type {
  CreatePagePayload,
  ListPagePayload,
  UpdatePagePayload,
} from "../../types/page/page.payload";

const initialState: PageSliceType = {
  loading: {
    list: false,
    detail: false,
    create: false,
    update: false,
    delete: false,
  },
  error: null,
  page: null,
  pages: [],
  pagination: null,
};

export const getPages = createApiThunk<ListPageResponse, ListPagePayload>(
  "page/getPages",
  async (query) =>
    (await axiosClient.get(API.page.list, { params: query })).data,
  "Lấy danh sách trang thất bại",
);

export const createPage = createApiThunk<
  CreatePageResponse,
  CreatePagePayload
>(
  "page/createPage",
  async (data) => (await axiosClient.post(API.page.create, data)).data,
  "Tạo trang thất bại",
);

export const getPage = createApiThunk<GetPageResponse, string>(
  "page/getPage",
  async (id) => (await axiosClient.get(API.page.detail(id))).data,
  "Lấy thông tin trang thất bại",
);

export const updatePage = createApiThunk<
  UpdatePageResponse,
  { id: string; data: UpdatePagePayload }
>(
  "page/updatePage",
  async ({ id, data }) =>
    (await axiosClient.put(API.page.update(id), data)).data,
  "Cập nhật trang thất bại",
);

export const deletePage = createApiThunk<string, string>(
  "page/deletePage",
  async (id) => {
    await axiosClient.delete(API.page.delete(id));
    return id;
  },
  "Xóa trang thất bại",
);

const pageSlice = createSlice({
  name: "page",
  initialState,
  reducers: {
    clearPage: (state) => {
      state.page = null;
    },
  },
  extraReducers: (builder) => {
    addAsyncCases({
      builder,
      thunk: getPages,
      loadingKey: "list",
      fallbackError: "Lấy danh sách trang thất bại",
      onSuccess: (state, payload) => {
        state.pages = payload.pages;
        state.pagination = payload.pagination;
      },
    });
    addAsyncCases({
      builder,
      thunk: createPage,
      loadingKey: "create",
      fallbackError: "Tạo trang thất bại",
      onSuccess: (state, payload) => {
        state.pages.unshift(payload.page);
      },
    });
    addAsyncCases({
      builder,
      thunk: getPage,
      loadingKey: "detail",
      fallbackError: "Lấy thông tin trang thất bại",
      onSuccess: (state, payload) => {
        state.page = payload.page;
      },
    });
    addAsyncCases({
      builder,
      thunk: updatePage,
      loadingKey: "update",
      fallbackError: "Cập nhật trang thất bại",
      onSuccess: (state, payload) => {
        const updated = payload.page;
        const idx = state.pages.findIndex((p) => p._id === updated._id);
        if (idx !== -1) state.pages[idx] = updated;
      },
    });
    addAsyncCases({
      builder,
      thunk: deletePage,
      loadingKey: "delete",
      fallbackError: "Xóa trang thất bại",
      onSuccess: (state, payload) => {
        state.pages = state.pages.filter((p) => p._id !== payload);
      },
    });
  },
});

export const { clearPage } = pageSlice.actions;
export default pageSlice.reducer;
